import { useCacheStore } from '~/stores/useCacheStore'
import { useNotification } from '~/composables/ShowNotification'

export const useOFetchCustom = (
    url: string,
    withoutAbortPrevRequest: boolean = false,
    withCachingRequest: boolean = false,
    withNotification: boolean = false,
    notificationMessage?: string
): {
    data: Ref<any>
    error: Ref<null | Error>
    loading: Ref<boolean>
    setUrl: (newUrl: string) => void
    post: (
        body?: any,
        options?: {
            headers?: Record<string, string | number>
            onSuccess?: (res: any) => void
            onError?: (err: any) => void
            onFinish?: () => void
        }
    ) => Promise<void>
    get: (
        params?: any,
        options?: {
            headers?: Record<string, string | number>
            onSuccess?: (res: any) => void
            onError?: (err: any) => void
            onFinish?: () => void
        }
    ) => Promise<void>
    put: (
        body?: any,
        options?: {
            headers?: Record<string, string | number>
            onSuccess?: (res: any) => void
            onError?: (err: any) => void
            onFinish?: () => void
        }
    ) => Promise<void>
    destroy: (
        body?: any,
        options?: {
            onSuccess?: (res: any) => void
            onError?: (err: any) => void
            onFinish?: () => void
        }
    ) => Promise<void>
} => {
    const cacheStore = useCacheStore()
    const notify = useNotification()
    const urlRef = ref(url)
    const data = ref(null)
    const error = ref(null)
    const loading = ref(false)
    const lastUpdate = ref<{ url: string, timestamp: number } | null>(null)

    const finalUrl = computed(() => "/api/" + urlRef.value)

    const client = useSanctumClient()
    const abortStore = useAbortControllerStore()
    function setUrl(newUrl: string) {
        urlRef.value = newUrl
    }

    function forceUpdate(newData: any) {
        if (Array.isArray(newData)) {
            const copy = [...JSON.parse(JSON.stringify(newData))];
            data.value = null;
            nextTick(() => {
                data.value = copy;
            });
        } else if (newData && typeof newData === 'object') {
            const copy = JSON.parse(JSON.stringify(newData));
            data.value = null;
            nextTick(() => {
                data.value = copy;
            });
        } else {
            data.value = newData;
        }
    }

    watch(lastUpdate, () => {
        if (lastUpdate.value) {
            const baseKey = lastUpdate.value.url.split('_')[0];
            const cachedData = cacheStore.get(lastUpdate.value.url) ?? cacheStore.get(baseKey);
            
            if (cachedData) {
                forceUpdate(cachedData);
            }
        }
    })

    const f = (
        options: Object,
        extraOptions?: {
            onSuccess?: (res: any) => void
            onError?: (err: any) => void
            onFinish?: () => void
            skipDataUpdate?: boolean
        }
    ) => {
        loading.value = true

        return client(finalUrl.value, {
            ...options,
            signal: !withoutAbortPrevRequest
                ? abortStore.addController(finalUrl.value).signal
                : undefined,
            onResponseError: ({ response }) => {
                error.value = response
                if (extraOptions?.onError != undefined) {
                    extraOptions?.onError(response)
                }
            },
            retry: 1,
        })
            .then((res: any) => {
                if (!extraOptions?.skipDataUpdate) {
                    data.value = res
                }

                new Promise(() => {
                    if (extraOptions?.onSuccess != undefined) {
                        extraOptions?.onSuccess(res)
                    }
                })
                return res
            })
            .catch((e) => {
                loading.value = false

                new Promise(() => {
                    if (extraOptions?.onError != undefined) {
                        extraOptions?.onError(e)
                    }
                })
            })
            .finally(() => {
                loading.value = false

                new Promise(() => {
                    if (extraOptions?.onFinish != undefined) {
                        extraOptions?.onFinish()
                    }
                })
            })
    }

    async function post(
        body?: any,
        options?: {
            onSuccess?: (res: any) => void
            onError?: (err: any) => void
            onFinish?: () => void
            headers?: Record<string, string | number>
        }
    ) {
        f(
            {
                method: "POST",
                body: body,
                withCredentials: true,
                headers: options?.headers,
            },
            options
        ).then(() => {})
    }

    function areDataEquivalent(newData: any, oldData: any): boolean {
        if (newData === oldData) return true;
        if (newData === null || oldData === null) return false;
        if (newData === undefined || oldData === undefined) return false;

        if (Array.isArray(newData) && Array.isArray(oldData)) {
            if (newData.length !== oldData.length) return false;
            
            for (let i = 0; i < newData.length; i++) {
                if (!areDataEquivalent(newData[i], oldData[i])) return false;
            }
            return true;
        }

        if (typeof newData === 'object' && typeof oldData === 'object') {
            const newKeys = Object.keys(newData);
            const oldKeys = Object.keys(oldData);
            
            if (newKeys.length !== oldKeys.length) return false;
            
            for (const key of newKeys) {
                if (!areDataEquivalent(newData[key], oldData[key])) return false;
            }
            return true;
        }

        return JSON.stringify(newData) === JSON.stringify(oldData);
    }

    async function get(
        params?: any,
        options?: {
            headers?: Record<string, string | number>
            onSuccess?: (res: any) => void
            onError?: (err: any) => void
            onFinish?: () => void
        }
    ) {
        if (withCachingRequest) {
            const cacheKey = finalUrl.value
            const cached = cacheStore.get(cacheKey)
            if (cached !== undefined) {
                data.value = cached
                loading.value = false
                if (options?.onSuccess) options.onSuccess(cached)
                if (options?.onFinish) options.onFinish()
                
                f(
                    {
                        method: "GET",
                        params: params,
                        withCredentials: true,
                        headers: options?.headers,
                    },
                    {
                        ...options,
                        skipDataUpdate: withNotification,
                        onSuccess: (res: any) => {
                            if (!areDataEquivalent(res, cached)) {
                                cacheStore.set(cacheKey, res)
                                
                                if (withNotification) {
                                    notify('info', notificationMessage ?? 'Hay datos nuevos disponibles', [
                                        {
                                            label: 'Actualizar ahora',
                                            color: 'white',
                                            handler: () => {
                                                forceUpdate(res);
                                                
                                                lastUpdate.value = {
                                                    url: cacheKey,
                                                    timestamp: Date.now()
                                                }
                                                
                                                if (options?.onSuccess) options.onSuccess(res)
                                            }
                                        }
                                    ])
                                } else {
                                    forceUpdate(res);
                                    
                                    lastUpdate.value = {
                                        url: cacheKey,
                                        timestamp: Date.now()
                                    }
                                    if (options?.onSuccess) options.onSuccess(res)
                                }
                            }
                        }
                    }
                )
                return
            }
        }

        return f(
            {
                method: "GET",
                params: params,
                withCredentials: true,
                headers: options?.headers,
            },
            {
                ...options,
                onSuccess: (res: any) => {
                    if (withCachingRequest) {
                        const cacheKey = finalUrl.value
                        cacheStore.set(cacheKey, res)
                    }
                    if (options?.onSuccess) options.onSuccess(res)
                }
            }
        )
    }

    async function put(
        body?: any,
        options?: {
            onSuccess?: (res: any) => void
            onError?: (err: any) => void
            onFinish?: () => void
            headers?: Record<string, string | number>
        }
    ) {
        f(
            {
                method: "PUT",
                body: body,
                withCredentials: true,
                headers: options?.headers,
            },
            options
        ).then(() => {})
    }

    function destroy(
        body?: any,
        options?: {
            onSuccess?: (res: any) => void
            onError?: (err: any) => void
            onFinish?: () => void
            headers?: Record<string, string | number>
        }
    ) {
        f(
            {
                method: "DELETE",
                body: body,
                withCredentials: true,
                headers: options?.headers,
            },
            options
        ).then(() => {})
    }

    return { data, error, loading, setUrl, post, get, put, destroy }
}
